import { create } from "zustand";
import type { ImpersonationUser } from "../types/impersonation.types";

export interface ImpersonationState {
	token: string | null;
	targetUser: ImpersonationUser | null;
	startedAt: string | null;
	/**
	 * True from the moment impersonation starts until the identity swap has been
	 * applied and navigation completes. While true, the platform guards show a
	 * transition overlay instead of redirecting, so a half-swapped state can
	 * never bounce the admin to /unauthorized.
	 */
	isSwapping: boolean;
	setImpersonation: (token: string, user: ImpersonationUser) => void;
	beginSwap: () => void;
	finishSwap: () => void;
	clearImpersonation: () => void;
}

/**
 * Impersonation is intentionally **in-memory only** (no `persist`, no
 * sessionStorage/localStorage). A page reload drops the impersonation token and
 * returns the admin to their own Keycloak session. This keeps a borrowed bearer
 * token out of web storage — see `.claude/rules/security.md`.
 */
export const useImpersonationStore = create<ImpersonationState>()((set) => ({
	token: null,
	targetUser: null,
	startedAt: null,
	isSwapping: false,
	setImpersonation: (token, targetUser) =>
		set({
			token,
			targetUser,
			startedAt: new Date().toISOString(),
			isSwapping: true,
		}),
	beginSwap: () => set({ isSwapping: true }),
	finishSwap: () => set({ isSwapping: false }),
	clearImpersonation: () =>
		set({
			token: null,
			targetUser: null,
			startedAt: null,
			isSwapping: false,
		}),
}));

/**
 * Synchronous getter used by the axios interceptor to read the current
 * impersonation token from in-memory store state.
 */
export function getImpersonationTokenFromStore(): string | null {
	return useImpersonationStore.getState().token;
}
